// Programmatic canvas art — chicken fighters, arena and UI primitives. Fully
// original and self-contained (RIGHTS.md: no source asset ships). Deliberately
// avoids per-frame shadowBlur for mobile GPU budget; "glow" is faked with
// translucent radial gradients.

import { PALETTE, FONT_DISPLAY, FONT_MONO } from "../game/config";
import type { Fighter } from "../game/roster";
import type { Assets, AssetKey } from "../core/assets";
import { easeOutBack } from "../core/tween";
import { sheetExcluded, sheetFallbackChain } from "./roster-sheets";
import type { RosterSheet } from "./roster-sheets";
import { anchorPlacement } from "./anchor";
import type { AnchorPlace } from "./anchor";

export type Pose = "idle" | "talon" | "wing" | "beak" | "hurt" | "ko" | "win" | "attack" | "special";

// ---- Drop-in PNG fighter art (manifest + per-class roster sheets) -------------
//
// Per-class sheets (public/assets/roster/<id>_{idle,attack,special}.png) render
// each of the ten classes with its own identity:
//   idle / hurt / ko → <id>_idle    · weapon attacks → <id>_attack
//   special / win    → <id>_special (win pose reuses the special sheet)
//   talon/wing/beak  → <id>_{talon,wing,beak} (lazily loaded per match via
//                      Assets.loadThrows; a miss falls back to the class's own
//                      ATTACK sheet — still per-class art — and only then to
//                      the base fighter_rock/paper/scissors art, NEVER the
//                      idle sheet; the RPS hand reads via the drawHandSign
//                      reveal badge either way)
// A missing class file degrades to the base fighter art + class tint; a missing
// base file falls back to the procedural canvas painter. The opponent side is
// mirrored (facing flip); when it renders from the SHARED base art it gets the
// purple side-tint so the two sides read apart, and when it renders from its own
// class sheet a lighter purple rim-tint keeps side readability without hiding
// the class colours. All variants are cached offscreen canvases — no per-frame
// compositing beyond one drawImage.

let assetSource: Assets | null = null;

/** Registers the loaded asset manifest as the fighter-art source. */
export function registerFighterArt(assets: Assets): void {
  assetSource = assets;
}

const POSE_TO_ASSET: Record<Pose, AssetKey> = {
  idle: "fighter_idle",
  talon: "fighter_rock",
  wing: "fighter_paper",
  beak: "fighter_scissors",
  hurt: "fighter_idle",
  ko: "fighter_idle",
  win: "fighter_idle",
  attack: "fighter_idle",
  special: "fighter_idle",
};

// Sheet policy (which sheets render + in-class fallback order) lives in
// ./roster-sheets so core/assets.ts shares the exact same table; the 2026-07
// art-audit repaint backlog is documented there.

// Diagnostic hook (read by tests-e2e/reveal-sprites.spec.ts and the reveal
// diagnosis driver): the art source of the LAST drawChicken call for each
// (classId, pose) — "roster:<id>:<sheet>", "base:<assetKey>" or "canvas".
// Plain-object writes, no per-frame allocation.
const ART_SOURCE_LOG: Record<string, string> = {};
(globalThis as { __rsbArtLog?: Record<string, string> }).__rsbArtLog = ART_SOURCE_LOG;

/** Which roster sheet a pose renders from (null = no class sheet applies). */
function rosterPhaseFor(pose: Pose): RosterSheet | null {
  switch (pose) {
    case "idle":
    case "hurt":
    case "ko":
      return "idle";
    case "attack":
      return "attack";
    case "special":
    case "win":
      return "special";
    case "talon":
    case "wing":
    case "beak":
      return pose; // per-class throw sprite (lazily loaded per match)
    default:
      return null;
  }
}

/**
 * The class sheet for (classId, pose), walking the in-class fallback chain
 * (roster-sheets.ts): attack/special → idle; a missing THROW sheet falls back
 * to the class's own attack sheet (still per-class art — the thrown RPS hand
 * reads via the drawHandSign reveal badge) before dropping to the shared base
 * fighter_rock/paper/scissors art. Null = no class sheet loaded at all.
 */
function classImage(
  classId: string,
  pose: Pose,
): { img: HTMLImageElement; key: string; sheet: RosterSheet } | null {
  const phase = rosterPhaseFor(pose);
  if (!phase || !assetSource) return null;
  for (const sheet of sheetFallbackChain(phase)) {
    if (sheetExcluded(classId, sheet)) continue;
    const img = assetSource.getRoster(classId, sheet);
    if (img) return { img, key: `roster:${classId}:${sheet}`, sheet };
  }
  return null;
}

/**
 * The roster idle portrait for a class (fighter-select cards), if it loaded —
 * tinted with the class colour so cards match the in-fight art. Null when the
 * sheet is missing or excluded (callers fall back to drawChicken).
 */
export function fighterPortraitImg(
  classId: string,
  body = "",
): HTMLImageElement | HTMLCanvasElement | null {
  if (sheetExcluded(classId, "idle")) return null;
  const img = assetSource?.getRoster(classId, "idle") ?? null;
  if (!img) return null;
  if (!body) return img;
  return imgVariant(img, `roster:${classId}:idle`, "classsheet", body) as
    | HTMLImageElement
    | HTMLCanvasElement;
}

type ImgVariantKind = "tint" | "tintlite" | "white" | "sil" | "class" | "classsheet" | "class2";
const IMG_VARIANTS = new Map<string, HTMLCanvasElement>();
const IMG_VARIANTS_MAX = 48; // LRU cap — size-bucketed variants stay bounded

/** Anchoring-math version — part of every sized variant cache key. */
const ANCHOR_V = "a2";

// Sprite-metrics keys for the shared base sheets (art/anchor.ts); a key
// missing here (logo/arena) simply takes the legacy centered fit.
const BASE_METRICS_KEY: Partial<Record<AssetKey, string>> = {
  fighter_idle: "base:idle",
  fighter_rock: "base:rock",
  fighter_paper: "base:paper",
  fighter_scissors: "base:scissors",
};
const BASE_IDLE_KEY = "base:idle";

// Reusable placement scratch — anchorPlacement() writes into this every draw
// call (single-threaded renderer), so the hot path allocates nothing.
const PLACE: AnchorPlace = { x: 0, y: 0, w: 0, h: 0 };

/** Device pixel ratio the game renders at (App caps at 2). */
function artDpr(): number {
  return Math.min(2, window.devicePixelRatio || 1);
}

/**
 * Tinted/flat variant of a fighter image, cached as an offscreen canvas.
 * When `dw`/`dh` (device px) are given the variant is PRE-SCALED to that size,
 * so the per-frame blit is 1:1 device pixels instead of a full-resolution
 * downscale of the source PNG every frame (the old hot-path cost).
 */
function imgVariant(
  img: HTMLImageElement,
  key: string,
  kind: ImgVariantKind,
  color = "",
  dw = 0,
  dh = 0,
): CanvasImageSource {
  // ANCHOR_V tags the sized variants so an anchoring-math bump never reuses a
  // canvas pre-scaled under the previous geometry.
  const ck = dw > 0 ? `${key}|${kind}|${color}|${dw}x${dh}|${ANCHOR_V}` : `${key}|${kind}|${color}`;
  const hit = IMG_VARIANTS.get(ck);
  if (hit) {
    // LRU bump.
    IMG_VARIANTS.delete(ck);
    IMG_VARIANTS.set(ck, hit);
    return hit;
  }
  const c = document.createElement("canvas");
  c.width = dw > 0 ? dw : img.width;
  c.height = dh > 0 ? dh : img.height;
  const g = c.getContext("2d");
  if (!g) return img;
  g.drawImage(img, 0, 0, c.width, c.height);
  // tint/tintlite/class* = colour overlay ON the art; white/sil = flat fills.
  const atop = kind !== "white" && kind !== "sil";
  g.globalCompositeOperation = atop ? "source-atop" : "source-in";
  if (kind === "tint") g.fillStyle = "rgba(139,92,246,0.48)";
  else if (kind === "tintlite") g.fillStyle = "rgba(139,92,246,0.20)";
  else if (kind === "class") {
    const [r, gg, b] = hexToRgb(color || "#8b5cf6");
    g.fillStyle = `rgba(${r},${gg},${b},0.40)`;
  } else if (kind === "classsheet" || kind === "class2") {
    // Class-colour wash over a class sheet so the declared roster colour
    // (roster.ts body) actually reads on the suit — card borders / stat bars /
    // FX colours and the art agree, and the yellow-suit classes separate.
    const [r, gg, b] = hexToRgb(color || "#8b5cf6");
    g.fillStyle = `rgba(${r},${gg},${b},0.35)`;
  } else g.fillStyle = kind === "white" ? "#ffffff" : "#181226";
  g.fillRect(0, 0, c.width, c.height);
  if (kind === "class2") {
    // Opponent side: purple rim over the class wash keeps side readability.
    g.fillStyle = "rgba(139,92,246,0.20)";
    g.fillRect(0, 0, c.width, c.height);
  }
  IMG_VARIANTS.set(ck, c);
  if (IMG_VARIANTS.size > IMG_VARIANTS_MAX) {
    const oldest = IMG_VARIANTS.keys().next().value;
    if (oldest !== undefined) {
      const old = IMG_VARIANTS.get(oldest);
      if (old) {
        old.width = 0;
        old.height = 0;
      }
      IMG_VARIANTS.delete(oldest);
    }
  }
  return c;
}

// ---- UI primitives ------------------------------------------------------------

export function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

export interface ButtonStyle {
  fill?: string;
  border?: string;
  text?: string;
  mono?: boolean;
  size?: number;
  disabled?: boolean;
  active?: boolean;
  hover?: boolean;
}

export function drawButton(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  label: string,
  s: ButtonStyle = {},
): void {
  const fill = s.disabled ? "#1a1a24" : s.active ? PALETTE.purple : (s.fill ?? PALETTE.bg2);
  const border = s.disabled ? "#2a2a38" : s.active ? PALETTE.purpleLt : (s.border ?? PALETTE.purple);
  ctx.save();
  if (s.hover && !s.disabled) {
    const g = ctx.createRadialGradient(x + w / 2, y + h / 2, 0, x + w / 2, y + h / 2, w * 0.7);
    g.addColorStop(0, "rgba(139,92,246,0.35)");
    g.addColorStop(1, "rgba(139,92,246,0)");
    ctx.fillStyle = g;
    ctx.fillRect(x - 8, y - 8, w + 16, h + 16);
  }
  roundRect(ctx, x, y, w, h, Math.min(14, h / 3));
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.lineWidth = 2;
  ctx.strokeStyle = border;
  ctx.stroke();
  ctx.fillStyle = s.disabled ? "#5a5a6a" : (s.text ?? PALETTE.text);
  ctx.font = `${s.mono ? "700 " : "800 "}${s.size ?? 20}px ${s.mono ? FONT_MONO : FONT_DISPLAY}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(label, x + w / 2, y + h / 2 + 1);
  ctx.restore();
}

export function drawBar(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  frac: number,
  fill: string,
  bg = "#20202c",
): void {
  const f = Math.max(0, Math.min(1, frac));
  roundRect(ctx, x, y, w, h, h / 2);
  ctx.fillStyle = bg;
  ctx.fill();
  if (f > 0) {
    ctx.save();
    roundRect(ctx, x, y, w, h, h / 2);
    ctx.clip();
    ctx.fillStyle = fill;
    ctx.fillRect(x, y, w * f, h);
    ctx.restore();
  }
  ctx.lineWidth = 1.5;
  ctx.strokeStyle = "rgba(255,255,255,0.15)";
  roundRect(ctx, x, y, w, h, h / 2);
  ctx.stroke();
}

export function text(
  ctx: CanvasRenderingContext2D,
  str: string,
  x: number,
  y: number,
  opts: { size?: number; color?: string; mono?: boolean; align?: CanvasTextAlign; weight?: number } = {},
): void {
  ctx.font = `${opts.weight ?? 700} ${opts.size ?? 18}px ${opts.mono ? FONT_MONO : FONT_DISPLAY}`;
  ctx.fillStyle = opts.color ?? PALETTE.text;
  ctx.textAlign = opts.align ?? "left";
  ctx.textBaseline = "alphabetic";
  ctx.fillText(str, x, y);
}

// ---- Arena --------------------------------------------------------------------
//
// Layered-canvas cache: the static parts (cover-fit background image, or the
// night gradient + sawdust floor + ring) are rendered ONCE per size into
// offscreen device-pixel canvases and blitted per frame. Only the two swaying
// spotlights stay dynamic — each is a pre-rendered radial-gradient sprite
// blitted at its sway position, replacing two full-screen gradient fills per
// frame with two cheap draws. Rebuilds only on resize / bg-image change.

interface ArenaCache {
  key: string;
  base: HTMLCanvasElement; // bg image OR night gradient (full frame)
  floor: HTMLCanvasElement | null; // floor strip drawn OVER the spotlights
  spot: HTMLCanvasElement | null; // one spotlight sprite
  floorY: number;
}

let arenaCache: ArenaCache | null = null;

/** Drops the cached arena layers (scene dispose / test hygiene). */
export function disposeArenaCache(): void {
  if (!arenaCache) return;
  for (const c of [arenaCache.base, arenaCache.floor, arenaCache.spot]) {
    if (c) {
      c.width = 0;
      c.height = 0;
    }
  }
  arenaCache = null;
}

function buildArenaCache(w: number, h: number, img: HTMLImageElement | null, key: string): ArenaCache {
  const dpr = artDpr();
  const mk = (cw: number, ch: number): [HTMLCanvasElement, CanvasRenderingContext2D | null] => {
    const c = document.createElement("canvas");
    c.width = Math.max(1, Math.round(cw * dpr));
    c.height = Math.max(1, Math.round(ch * dpr));
    const g = c.getContext("2d");
    g?.scale(dpr, dpr);
    return [c, g];
  };

  const [base, bg] = mk(w, h);
  const floorY = h * 0.72;
  if (img && bg) {
    // Cover-fit ONCE at target resolution (was a per-frame full-res downscale).
    const s = Math.max(w / img.width, h / img.height);
    const iw = img.width * s;
    const ih = img.height * s;
    bg.drawImage(img, (w - iw) / 2, (h - ih) / 2, iw, ih);
    return { key, base, floor: null, spot: null, floorY };
  }
  if (bg) {
    const grad = bg.createLinearGradient(0, 0, 0, h);
    grad.addColorStop(0, "#141024");
    grad.addColorStop(0.55, PALETTE.bg);
    grad.addColorStop(1, "#05050a");
    bg.fillStyle = grad;
    bg.fillRect(0, 0, w, h);
  }

  // Floor strip (drawn over the spotlights, preserving the original order).
  const floorH = h - floorY;
  const [floor, fg] = mk(w, floorH);
  if (fg) {
    const grad = fg.createLinearGradient(0, 0, 0, floorH);
    grad.addColorStop(0, "#2a2233");
    grad.addColorStop(1, "#0c0a12");
    fg.fillStyle = grad;
    fg.fillRect(0, 0, w, floorH);
    fg.strokeStyle = "rgba(224,146,47,0.35)";
    fg.lineWidth = 3;
    fg.beginPath();
    fg.ellipse(w / 2, floorH * 0.5, w * 0.42, floorH * 0.42, 0, 0, Math.PI * 2);
    fg.stroke();
  }

  // One spotlight sprite: radial gradient centred near its top, sized so the
  // per-frame blit covers the same footprint the full-screen fill lit up.
  const spotW = h * 1.9;
  const spotH = h * 1.1;
  const [spot, sg] = mk(spotW, spotH);
  if (sg) {
    const grad = sg.createRadialGradient(spotW / 2, 0, 0, spotW / 2, h * 0.7, h * 0.9);
    grad.addColorStop(0, "rgba(139,92,246,0.18)");
    grad.addColorStop(1, "rgba(139,92,246,0)");
    sg.fillStyle = grad;
    sg.fillRect(0, 0, spotW, spotH);
  }
  return { key, base, floor, spot, floorY };
}

export function drawArena(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  img: HTMLImageElement | null,
  t: number,
): void {
  const key = `${w}x${h}|${artDpr()}|${img ? img.src : "-"}`;
  if (!arenaCache || arenaCache.key !== key) {
    disposeArenaCache();
    arenaCache = buildArenaCache(w, h, img, key);
  }
  const a = arenaCache;
  ctx.drawImage(a.base, 0, 0, w, h);
  if (a.spot) {
    const spotW = h * 1.9;
    const spotH = h * 1.1;
    for (let i = 0; i < 2; i++) {
      const cx = w * (0.3 + 0.4 * i) + Math.sin(t * 0.6 + i) * w * 0.05;
      ctx.drawImage(a.spot, cx - spotW / 2, -h * 0.1, spotW, spotH);
    }
  }
  if (a.floor) ctx.drawImage(a.floor, 0, a.floorY, w, h - a.floorY);
}

// ---- Chick-hood fighter (site mascot) -----------------------------------------
//
// Both fighters ARE the chickencto.fun mascot: a plush yellow chick-costume hood
// (round, soft scalloped face opening, stubby comb, orange beak on the hood) worn
// over a pale face with two huge dark eye patches and a wide red grin full of
// teeth. Cute plush outside, unsettling inside. The player wears the yellow hood;
// the opponent (facing = -1) wears the site-purple one so the sides read apart.
//
// Performance: every (variant, pose, size, crest) is painted ONCE into an
// offscreen canvas (2x supersampled) and blitted per frame with cheap transforms
// (bob / lean / KO tip-over). No per-frame path building, no shadowBlur. The hit
// flash is a cached white-silhouette copy drawn with globalAlpha. Scenes that
// leave the arena call disposeFighterArt() from dispose() to drop the cache.

const HOOD_P1 = "#f5c542"; // plush yellow (site mascot)
const HOOD_P2 = "#8b5cf6"; // site purple (opponent variant)
const COMB = "#e2413f"; // orange/red plush comb
const BEAK = "#f28c28"; // orange beak
const FACE = "#f4efe4"; // pale face inside the hood
const PATCH = "#191322"; // big dark eye patches
const MOUTH = "#520d15"; // grin interior
const LIPS = "#d42a3d"; // red grin rim
const TEETH = "#f6f3ea";

interface HoodColors {
  base: string;
  light: string;
  dark: string;
  rim: string;
  trim: string;
}

function hoodColors(base: string): HoodColors {
  return {
    base,
    light: lighten(base, 0.22),
    dark: darken(base, 0.18),
    rim: darken(base, 0.4),
    trim: lighten(base, 0.32),
  };
}

const HC_P1 = hoodColors(HOOD_P1);
const HC_P2 = hoodColors(HOOD_P2);

// Sprite-local extents in units of `scale` (origin = between the feet, y up-).
const EXT_L = 2.2;
const EXT_R = 2.5;
const EXT_T = 3.45;
const EXT_B = 0.15;
const SS = 2; // supersample factor (covers DPR<=2 blits)
const CACHE_MAX = 28;

const spriteCache = new Map<string, HTMLCanvasElement>();

/** Frees all pre-rendered fighter canvases. Call from scene dispose(). */
export function disposeFighterArt(): void {
  for (const c of spriteCache.values()) {
    c.width = 0;
    c.height = 0;
  }
  spriteCache.clear();
  for (const c of IMG_VARIANTS.values()) {
    c.width = 0;
    c.height = 0;
  }
  IMG_VARIANTS.clear();
  disposeArenaCache();
}

function cachePut(key: string, c: HTMLCanvasElement): void {
  spriteCache.set(key, c);
  if (spriteCache.size > CACHE_MAX) {
    const oldest = spriteCache.keys().next().value;
    if (oldest !== undefined) {
      const old = spriteCache.get(oldest);
      if (old) {
        old.width = 0;
        old.height = 0;
      }
      spriteCache.delete(oldest);
    }
  }
}

function cacheGet(key: string): HTMLCanvasElement | undefined {
  const c = spriteCache.get(key);
  if (c) {
    // LRU bump.
    spriteCache.delete(key);
    spriteCache.set(key, c);
  }
  return c;
}

function fighterSprite(variant: "p1" | "p2", poseIn: Pose, sr: number, crest: Fighter["crest"]): HTMLCanvasElement {
  // The procedural painter predates the attack/special sheets — map them onto
  // its closest silhouettes so the canvas fallback still reads.
  const pose: Pose = poseIn === "attack" ? "talon" : poseIn === "special" ? "win" : poseIn;
  const key = `${variant}|${pose}|${sr}|${crest}`;
  const hit = cacheGet(key);
  if (hit) return hit;
  const u = sr * SS;
  const c = document.createElement("canvas");
  c.width = Math.ceil((EXT_L + EXT_R) * u);
  c.height = Math.ceil((EXT_T + EXT_B) * u);
  const g = c.getContext("2d");
  if (g) {
    g.translate(EXT_L * u, EXT_T * u);
    paintFighter(g, u, pose, variant === "p2" ? HC_P2 : HC_P1, crest);
  }
  cachePut(key, c);
  return c;
}

function flatSprite(
  variant: "p1" | "p2",
  pose: Pose,
  sr: number,
  crest: Fighter["crest"],
  color: string,
  suffix: string,
): HTMLCanvasElement {
  const key = `${variant}|${pose}|${sr}|${crest}|${suffix}`;
  const hit = cacheGet(key);
  if (hit) return hit;
  const base = fighterSprite(variant, pose, sr, crest);
  const c = document.createElement("canvas");
  c.width = base.width;
  c.height = base.height;
  const g = c.getContext("2d");
  if (g) {
    g.drawImage(base, 0, 0);
    g.globalCompositeOperation = "source-in";
    g.fillStyle = color;
    g.fillRect(0, 0, c.width, c.height);
  }
  cachePut(key, c);
  return c;
}

function whiteSprite(variant: "p1" | "p2", pose: Pose, sr: number, crest: Fighter["crest"]): HTMLCanvasElement {
  return flatSprite(variant, pose, sr, crest, "#ffffff", "w");
}

/** Extra per-draw animation controls (all optional; defaults = old behaviour). */
export interface ChickenAnim {
  /** Vertical offset in px (negative = lifted): entrance hops, win bounces. */
  dy?: number;
  /** KO tumble progress 0..1 — animates the tip-over instead of snapping. */
  koT?: number;
  /** Squash/stretch multipliers about the feet anchor (1 = neutral). */
  sx?: number;
  sy?: number;
  /** Extra lean radians on top of the pose lean (motion-arc rotation). */
  rot?: number;
  /** Skip the ground shadow (used for smear afterimages). */
  noShadow?: boolean;
  /**
   * Cross-fade source: the pose being LEFT. When set (and different from the
   * drawn pose) it renders underneath at alpha (1-blend) while the new pose
   * fades in at `blend` — both through the same anchored path, so residual
   * silhouette differences read as motion, not a swap glitch.
   */
  prevPose?: Pose;
  /** Cross-fade progress 0..1 (1 / absent = no fade; new pose fully in). */
  blend?: number;
  /**
   * Smear afterimages: world-space offsets drawn BEHIND the fighter at reduced
   * alpha (entries with alpha ≤ 0.01 are skipped — callers reuse the array).
   */
  ghosts?: readonly { dx: number; dy: number; alpha: number }[];
}

/** Small stable hash for per-class idle-bob variance. */
function idHash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

/**
 * Draws a fighter with its feet at (cx, baseY). `facing` = 1 faces right
 * (player), -1 faces left (opponent). `scale` ≈ body radius in px. `flash`
 * 0..1 whites the sprite out for hits/KO. Per-class roster sheets render when
 * loaded; otherwise base art + class tint; otherwise the canvas painter.
 */
export function drawChicken(
  ctx: CanvasRenderingContext2D,
  cx: number,
  baseY: number,
  scale: number,
  f: Fighter,
  pose: Pose,
  facing: 1 | -1,
  t: number,
  flash = 0,
  silhouette = false,
  anim: ChickenAnim = {},
): void {
  const variant = facing === -1 ? "p2" : "p1";
  const dy = anim.dy ?? 0;

  // Smear afterimages first, BEHIND the fighter: same pose/transform chain at
  // offset positions, no shadow, faded. Each ghost is a single cached-canvas
  // blit — the recursion depth is exactly one (ghost draws carry no ghosts).
  if (anim.ghosts && !silhouette) {
    const prevAlpha = ctx.globalAlpha;
    for (const gh of anim.ghosts) {
      if (gh.alpha <= 0.01) continue;
      ctx.globalAlpha = prevAlpha * gh.alpha;
      drawChicken(ctx, cx + gh.dx, baseY + gh.dy, scale, f, pose, facing, t, 0.4, false, {
        dy: anim.dy,
        koT: anim.koT,
        sx: anim.sx,
        sy: anim.sy,
        rot: anim.rot,
        noShadow: true,
      });
    }
    ctx.globalAlpha = prevAlpha;
  }

  // Entry alpha — ghost draws arrive pre-faded; every overlay must inherit it.
  const baseAlpha = ctx.globalAlpha;

  ctx.save();
  ctx.translate(cx, baseY);

  // Ground shadow before any body transform so it stays on the floor; it
  // shrinks slightly while the fighter is airborne (entrance hop / win bounce).
  if (!anim.noShadow) {
    const air = Math.min(1, Math.abs(dy) / (scale * 1.2));
    ctx.fillStyle = `rgba(0,0,0,${0.35 * (1 - air * 0.5)})`;
    ctx.beginPath();
    ctx.ellipse(0, 4, scale * (pose === "ko" ? 1.5 : 1.15) * (1 - air * 0.25), scale * 0.26, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.scale(facing, 1);
  ctx.translate(0, dy);
  if (pose === "ko") {
    // Tipped over on its back; `koT` animates the fall (default = landed).
    const k = easeOutBack(anim.koT ?? 1);
    ctx.translate(scale * 1.1 * k, -scale * 0.9 * k);
    ctx.rotate(-1.45 * k);
  } else {
    const lean =
      pose === "hurt" ? -0.16 : pose === "beak" ? 0.1 : pose === "talon" || pose === "attack" ? 0.06 : 0;
    ctx.rotate(lean + (anim.rot ?? 0));
    // Squash/stretch about the feet anchor (breathing, anticipation, release).
    const sx = anim.sx ?? 1;
    const sy = anim.sy ?? 1;
    if (sx !== 1 || sy !== 1) ctx.scale(sx, sy);
    // Idle bob with per-class variance (rate/amplitude/phase from the id hash).
    const h = idHash(f.id);
    const rate = 3.4 + (h % 5) * 0.35;
    const amp = 0.032 + ((h >> 3) % 4) * 0.007;
    ctx.translate(0, Math.sin(t * rate + (h % 7)) * scale * amp);
  }

  // Pose swap cross-fade: the pose being left fades out UNDER the new pose —
  // both through the same anchored path — so residual silhouette differences
  // read as motion, not a swap glitch. KO keeps the hard swap (the tumble
  // transform belongs to the ko pose alone); silhouettes never blend.
  const blend = anim.blend === undefined ? 1 : Math.max(0, Math.min(1, anim.blend));
  const fading =
    anim.prevPose !== undefined && anim.prevPose !== pose && blend < 1 && pose !== "ko" && !silhouette;
  if (fading) {
    paintPoseArt(ctx, f, anim.prevPose as Pose, variant, scale, flash, silhouette, baseAlpha * (1 - blend), baseAlpha);
    paintPoseArt(ctx, f, pose, variant, scale, flash, silhouette, baseAlpha * blend, baseAlpha);
  } else {
    paintPoseArt(ctx, f, pose, variant, scale, flash, silhouette, baseAlpha, baseAlpha);
  }
  ctx.restore();
}

/**
 * Paints the art for ONE pose at the current transform (origin = the fighter's
 * x on the floor line, facing flip already applied). Anchored rendering
 * (art/anchor.ts): the sheet's measured feet row lands on the floor line, the
 * on-screen character height is the class idle's normalized height, and the
 * measured stance center sits at local x 0 — which also makes the facing flip
 * mirror around the STANCE center, not the trim-box center. `alpha` is the
 * absolute alpha for every blit (cross-fade layers draw at partial alpha); the
 * hit flash rides on top at alpha*flash. globalAlpha is left at `restore`.
 */
function paintPoseArt(
  ctx: CanvasRenderingContext2D,
  f: Fighter,
  pose: Pose,
  variant: "p1" | "p2",
  scale: number,
  flash: number,
  silhouette: boolean,
  alpha: number,
  restore: number,
): void {
  const targetH = (EXT_T + EXT_B) * scale;
  const footY = EXT_B * scale;

  // Per-class roster sheet first (mirrored via the facing flip).
  const cls = silhouette ? null : classImage(f.id, pose);
  if (cls && cls.img.width > 0 && cls.img.height > 0) {
    ART_SOURCE_LOG[`${f.id}|${pose}`] = cls.key;
    // Anchor on the sheet that actually renders (a fallback sheet anchors on
    // its OWN measured rows, not the requested pose's).
    const p = anchorPlacement(`${f.id}:${cls.sheet}`, `${f.id}:idle`, cls.img.width, cls.img.height, targetH, footY, PLACE);
    // Pre-scaled variants: composited once at draw size × DPR, blitted 1:1.
    const dpr = artDpr();
    const dw = Math.max(1, Math.round(p.w * dpr));
    const dh = Math.max(1, Math.round(p.h * dpr));
    // Both sides get the class-colour wash (roster.ts body) so the declared
    // class colour reads on the suit; the opponent adds the purple side rim.
    const src: CanvasImageSource =
      variant === "p2"
        ? imgVariant(cls.img, cls.key, "class2", f.body, dw, dh)
        : imgVariant(cls.img, cls.key, "classsheet", f.body, dw, dh);
    ctx.globalAlpha = alpha;
    ctx.drawImage(src, p.x, p.y, p.w, p.h);
    if (flash > 0) {
      ctx.globalAlpha = alpha * Math.min(1, flash);
      ctx.drawImage(imgVariant(cls.img, cls.key, "white", "", dw, dh), p.x, p.y, p.w, p.h);
    }
    ctx.globalAlpha = restore;
    return;
  }

  // Base manifest PNG path: p2 purple-tinted; a class-sheet MISS on a pose that
  // wanted one (idle/attack/special/win/…) gets the class tint so the ten
  // classes still read apart on shared art. Anchored via the base:* metrics
  // (a missing/stale metric entry degrades to the legacy centered fit).
  const assetKey = POSE_TO_ASSET[pose] ?? "fighter_idle";
  const img = assetSource?.get(assetKey) ?? assetSource?.get("fighter_idle") ?? null;
  if (img && img.width > 0 && img.height > 0) {
    if (!silhouette) ART_SOURCE_LOG[`${f.id}|${pose}`] = `base:${assetKey}`;
    const p = anchorPlacement(BASE_METRICS_KEY[assetKey] ?? "", BASE_IDLE_KEY, img.width, img.height, targetH, footY, PLACE);
    const dpr = artDpr();
    const dw = Math.max(1, Math.round(p.w * dpr));
    const dh = Math.max(1, Math.round(p.h * dpr));
    let src: CanvasImageSource;
    if (silhouette) src = imgVariant(img, assetKey, "sil", "", dw, dh);
    else if (variant === "p2") src = imgVariant(img, assetKey, "tint", "", dw, dh);
    else if (rosterPhaseFor(pose) !== null) src = imgVariant(img, assetKey, "class", f.body, dw, dh);
    else src = img; // unreachable in practice (every pose maps to a roster phase)
    ctx.globalAlpha = alpha;
    ctx.drawImage(src, p.x, p.y, p.w, p.h);
    if (flash > 0 && !silhouette) {
      ctx.globalAlpha = alpha * Math.min(1, flash);
      ctx.drawImage(imgVariant(img, assetKey, "white", "", dw, dh), p.x, p.y, p.w, p.h);
    }
    ctx.globalAlpha = restore;
    return;
  }

  // Procedural canvas fallback (no PNG for this key). Painted into a fixed
  // sprite box (EXT_* geometry) — consistent across poses, no anchoring needed.
  if (!silhouette) ART_SOURCE_LOG[`${f.id}|${pose}`] = "canvas";
  const sr = Math.max(6, Math.round(scale));
  const spr = silhouette
    ? flatSprite(variant, pose, sr, f.crest, "#181226", "s")
    : fighterSprite(variant, pose, sr, f.crest);
  const k = scale / (sr * SS);
  const ox = -EXT_L * scale;
  const oy = -EXT_T * scale;
  ctx.globalAlpha = alpha;
  ctx.drawImage(spr, ox, oy, spr.width * k, spr.height * k);
  if (flash > 0 && !silhouette) {
    const w = whiteSprite(variant, pose, sr, f.crest);
    ctx.globalAlpha = alpha * Math.min(1, flash);
    ctx.drawImage(w, ox, oy, w.width * k, w.height * k);
  }
  ctx.globalAlpha = restore;
}

function circle(g: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  g.beginPath();
  g.arc(x, y, r, 0, Math.PI * 2);
}

function ell(g: CanvasRenderingContext2D, x: number, y: number, rx: number, ry: number, rot = 0): void {
  g.beginPath();
  g.ellipse(x, y, rx, ry, rot, 0, Math.PI * 2);
}

function limb(g: CanvasRenderingContext2D, x0: number, y0: number, x1: number, y1: number, w: number, color: string): void {
  g.strokeStyle = color;
  g.lineWidth = w;
  g.lineCap = "round";
  g.beginPath();
  g.moveTo(x0, y0);
  g.lineTo(x1, y1);
  g.stroke();
}

/** Paints one fighter (facing +x) into sprite space. `u` = scale * SS px. */
function paintFighter(
  g: CanvasRenderingContext2D,
  u: number,
  pose: Pose,
  hc: HoodColors,
  crest: Fighter["crest"],
): void {
  // Face-opening geometry (slightly forward on the ball).
  const fx = 0.16 * u;
  const fy = -1.46 * u;
  const fr = 0.74 * u;

  // Baked purple glow (site accent) — replaces any shadowBlur cost.
  const glow = g.createRadialGradient(0, -1.5 * u, 0, 0, -1.5 * u, 1.95 * u);
  glow.addColorStop(0, "rgba(139,92,246,0.30)");
  glow.addColorStop(1, "rgba(139,92,246,0)");
  g.fillStyle = glow;
  g.fillRect(-EXT_L * u, -EXT_T * u, (EXT_L + EXT_R) * u, (EXT_T + EXT_B) * u);

  // Small gold legs + toes — the mascot is nearly all hood.
  g.strokeStyle = PALETTE.gold;
  g.lineCap = "round";
  for (const lx of [-0.42, 0.34]) {
    g.lineWidth = 0.11 * u;
    g.beginPath();
    g.moveTo(lx * u, -0.5 * u);
    g.lineTo(lx * u, 0);
    g.stroke();
    g.lineWidth = 0.07 * u;
    g.beginPath();
    g.moveTo(lx * u, 0);
    g.lineTo((lx + 0.17) * u, 0);
    g.moveTo(lx * u, 0);
    g.lineTo((lx - 0.11) * u, 0.02 * u);
    g.stroke();
  }

  // Plush tail nub + back wing nub.
  g.fillStyle = hc.dark;
  circle(g, -1.12 * u, -1.35 * u, 0.24 * u);
  g.fill();
  ell(g, -0.96 * u, -0.98 * u, 0.28 * u, 0.42 * u, 0.3);
  g.fill();

  // The hood ball — one soft round plush silhouette.
  const ballGrad = g.createLinearGradient(0, -2.75 * u, 0, -0.25 * u);
  ballGrad.addColorStop(0, hc.light);
  ballGrad.addColorStop(0.55, hc.base);
  ballGrad.addColorStop(1, hc.dark);
  g.fillStyle = ballGrad;
  ell(g, 0, -1.5 * u, 1.15 * u, 1.25 * u);
  g.fill();
  g.lineWidth = 0.045 * u;
  g.strokeStyle = hc.rim;
  g.stroke();
  // Soft fabric highlight.
  g.fillStyle = "rgba(255,255,255,0.13)";
  ell(g, -0.35 * u, -2.35 * u, 0.5 * u, 0.26 * u, 0.5);
  g.fill();

  // Stubby plush comb on top.
  g.fillStyle = COMB;
  circle(g, -0.34 * u, -2.7 * u, 0.15 * u);
  g.fill();
  circle(g, -0.09 * u, -2.85 * u, 0.19 * u);
  g.fill();
  circle(g, 0.17 * u, -2.72 * u, 0.15 * u);
  g.fill();

  // Scalloped plush trim around the face opening.
  g.fillStyle = hc.trim;
  for (let i = 0; i < 9; i++) {
    const a = (i / 9) * Math.PI * 2 + 0.35;
    circle(g, fx + Math.cos(a) * fr, fy + Math.sin(a) * fr, 0.17 * u);
    g.fill();
  }

  // The face inside the hood.
  const faceGrad = g.createRadialGradient(fx - 0.15 * u, fy - 0.25 * u, 0, fx, fy, fr * 1.15);
  faceGrad.addColorStop(0, lighten(FACE, 0.35));
  faceGrad.addColorStop(1, darken(FACE, 0.12));
  g.fillStyle = faceGrad;
  circle(g, fx, fy, fr - 0.02 * u);
  g.fill();
  g.lineWidth = 0.06 * u;
  g.strokeStyle = "rgba(0,0,0,0.28)"; // hood casting onto the face
  circle(g, fx, fy, fr - 0.05 * u);
  g.stroke();

  g.save();
  circle(g, fx, fy, fr - 0.03 * u);
  g.clip();
  paintFace(g, u, pose, fx, fy);
  g.restore();

  // Orange beak stitched on the hood, above the face opening (drawn after the
  // face so it sits on top of the plush trim like on the mascot).
  g.fillStyle = BEAK;
  g.beginPath();
  g.moveTo(-0.18 * u, -2.36 * u);
  g.quadraticCurveTo(0.16 * u, -2.54 * u, 0.5 * u, -2.36 * u); // plump top arc
  g.quadraticCurveTo(0.58 * u, -2.12 * u, 0.22 * u, -1.94 * u); // down to the tip
  g.quadraticCurveTo(-0.24 * u, -2.1 * u, -0.18 * u, -2.36 * u);
  g.closePath();
  g.fill();
  g.save();
  g.clip();
  g.fillStyle = "rgba(0,0,0,0.2)"; // shaded underside
  g.fillRect(-0.3 * u, -2.18 * u, u, 0.26 * u);
  g.restore();

  // Front wing per pose (over the ball so the limb reads attached).
  paintWing(g, u, pose, hc);

  // Small class emblem so the ten roster entries still read apart.
  paintCrest(g, u, crest);
}

function paintFace(g: CanvasRenderingContext2D, u: number, pose: Pose, fx: number, fy: number): void {
  const eyL = { x: fx - 0.3 * u, y: fy - 0.2 * u };
  const eyR = { x: fx + 0.32 * u, y: fy - 0.2 * u };

  // Huge dark eye patches with a faint smudged edge.
  g.fillStyle = PATCH;
  ell(g, eyL.x, eyL.y, 0.27 * u, 0.3 * u);
  g.fill();
  ell(g, eyR.x, eyR.y, 0.28 * u, 0.31 * u);
  g.fill();
  g.strokeStyle = "rgba(25,19,34,0.35)";
  g.lineWidth = 0.06 * u;
  ell(g, eyL.x, eyL.y, 0.31 * u, 0.34 * u);
  g.stroke();
  ell(g, eyR.x, eyR.y, 0.32 * u, 0.35 * u);
  g.stroke();

  if (pose === "ko") {
    // Knocked-out X eyes.
    g.strokeStyle = TEETH;
    g.lineWidth = 0.055 * u;
    g.lineCap = "round";
    for (const e of [eyL, eyR]) {
      const s = 0.12 * u;
      g.beginPath();
      g.moveTo(e.x - s, e.y - s);
      g.lineTo(e.x + s, e.y + s);
      g.moveTo(e.x + s, e.y - s);
      g.lineTo(e.x - s, e.y + s);
      g.stroke();
    }
  } else {
    // White eyes staring out of the patches.
    g.fillStyle = "#ffffff";
    circle(g, eyL.x, eyL.y, 0.14 * u);
    g.fill();
    circle(g, eyR.x, eyR.y, 0.15 * u);
    g.fill();
    const pr = pose === "hurt" ? 0.045 * u : 0.07 * u;
    g.fillStyle = "#0d0b12";
    circle(g, eyL.x + 0.03 * u, eyL.y + 0.01 * u, pr);
    g.fill();
    circle(g, eyR.x + 0.03 * u, eyR.y + 0.01 * u, pr);
    g.fill();
  }

  // Tiny nose wedge.
  g.fillStyle = "#cdc4b0";
  g.beginPath();
  g.moveTo(fx - 0.05 * u, fy + 0.06 * u);
  g.lineTo(fx + 0.07 * u, fy + 0.06 * u);
  g.lineTo(fx + 0.01 * u, fy + 0.16 * u);
  g.closePath();
  g.fill();

  // The wide grin — red rim, dark interior, two rows of pointed teeth.
  const mw = 0.56 * u; // half-width
  const my = fy + 0.24 * u; // corner height
  const mouth = (): void => {
    g.beginPath();
    g.moveTo(fx - mw, my);
    g.quadraticCurveTo(fx, my + 0.12 * u, fx + mw, my);
    g.quadraticCurveTo(fx + 0.44 * u, my + 0.46 * u, fx, my + 0.48 * u);
    g.quadraticCurveTo(fx - 0.44 * u, my + 0.46 * u, fx - mw, my);
    g.closePath();
  };
  mouth();
  g.fillStyle = MOUTH;
  g.fill();

  g.save();
  mouth();
  g.clip();
  const step = (2 * mw) / 8;
  // Top row of sharp teeth.
  g.fillStyle = TEETH;
  g.beginPath();
  g.moveTo(fx - mw, my - 0.2 * u);
  g.lineTo(fx - mw, my + 0.02 * u);
  for (let i = 0; i < 8; i++) {
    const x = fx - mw + i * step;
    g.lineTo(x + step / 2, my + 0.3 * u);
    g.lineTo(x + step, my + 0.02 * u);
  }
  g.lineTo(fx + mw, my - 0.2 * u);
  g.closePath();
  g.fill();
  // Bottom row, offset half a tooth, slightly dimmer.
  g.fillStyle = "#e8e1cf";
  g.beginPath();
  g.moveTo(fx - mw, my + 0.6 * u);
  g.lineTo(fx - mw, my + 0.46 * u);
  for (let i = 0; i < 8; i++) {
    const x = fx - mw + (i - 0.5) * step;
    g.lineTo(x + step / 2, my + 0.2 * u);
    g.lineTo(x + step, my + 0.46 * u);
  }
  g.lineTo(fx + mw, my + 0.6 * u);
  g.closePath();
  g.fill();
  g.restore();

  mouth();
  g.lineWidth = 0.07 * u;
  g.strokeStyle = LIPS;
  g.stroke();
}

function paintWing(g: CanvasRenderingContext2D, u: number, pose: Pose, hc: HoodColors): void {
  // Shoulder sits low on the ball so the limbs never cover the face.
  const shX = 0.55 * u;
  const shY = -0.95 * u;
  switch (pose) {
    case "talon": {
      // ROCK — stubby wing balled into a plush fist, thrust forward.
      limb(g, shX, shY, 1.38 * u, -1.02 * u, 0.42 * u, hc.dark);
      g.fillStyle = hc.base;
      circle(g, 1.5 * u, -1.02 * u, 0.34 * u);
      g.fill();
      g.lineWidth = 0.04 * u;
      g.strokeStyle = hc.rim;
      g.stroke();
      g.fillStyle = hc.light;
      circle(g, 1.61 * u, -1.16 * u, 0.09 * u);
      g.fill();
      circle(g, 1.69 * u, -1.02 * u, 0.09 * u);
      g.fill();
      circle(g, 1.61 * u, -0.88 * u, 0.09 * u);
      g.fill();
      // Impact arcs in site purple.
      g.strokeStyle = "rgba(196,181,253,0.8)";
      g.lineWidth = 0.05 * u;
      for (const r of [0.5, 0.66]) {
        g.beginPath();
        g.arc(1.5 * u, -1.02 * u, r * u, -0.55, 0.55);
        g.stroke();
      }
      break;
    }
    case "wing": {
      // PAPER — wing spread into a flat plush mitt.
      limb(g, shX, shY, 1.05 * u, -1.05 * u, 0.34 * u, hc.dark);
      g.fillStyle = hc.base;
      ell(g, 1.48 * u, -1.08 * u, 0.6 * u, 0.3 * u, -0.06);
      g.fill();
      g.lineWidth = 0.04 * u;
      g.strokeStyle = hc.rim;
      g.stroke();
      // Scalloped fingertip bumps.
      g.fillStyle = hc.base;
      circle(g, 2.02 * u, -1.26 * u, 0.13 * u);
      g.fill();
      circle(g, 2.1 * u, -1.1 * u, 0.13 * u);
      g.fill();
      circle(g, 2.02 * u, -0.94 * u, 0.13 * u);
      g.fill();
      // Seams.
      g.strokeStyle = hc.dark;
      g.lineWidth = 0.035 * u;
      g.beginPath();
      g.moveTo(1.1 * u, -1.08 * u);
      g.lineTo(1.94 * u, -1.22 * u);
      g.moveTo(1.1 * u, -1.08 * u);
      g.lineTo(1.94 * u, -0.96 * u);
      g.stroke();
      break;
    }
    case "beak": {
      // SCISSORS — two feather-tips split in a V.
      limb(g, shX, shY, 0.98 * u, -1.02 * u, 0.34 * u, hc.dark);
      limb(g, 0.96 * u, -1.02 * u, 1.74 * u, -1.44 * u, 0.24 * u, hc.base);
      limb(g, 0.96 * u, -1.02 * u, 1.74 * u, -0.64 * u, 0.24 * u, hc.base);
      g.fillStyle = hc.light;
      circle(g, 1.76 * u, -1.46 * u, 0.11 * u);
      g.fill();
      circle(g, 1.76 * u, -0.62 * u, 0.11 * u);
      g.fill();
      break;
    }
    case "win": {
      // Fist raised to the sky, clear of the face.
      limb(g, 0.85 * u, -1.1 * u, 1.35 * u, -2.4 * u, 0.4 * u, hc.dark);
      g.fillStyle = hc.base;
      circle(g, 1.41 * u, -2.52 * u, 0.3 * u);
      g.fill();
      g.lineWidth = 0.04 * u;
      g.strokeStyle = hc.rim;
      g.stroke();
      break;
    }
    default: {
      // idle / hurt / ko — stubby wing nub at the side.
      g.fillStyle = hc.dark;
      ell(g, 0.98 * u, -0.9 * u, 0.26 * u, 0.4 * u, -0.35);
      g.fill();
      g.lineWidth = 0.035 * u;
      g.strokeStyle = hc.rim;
      g.stroke();
    }
  }
}

function paintCrest(g: CanvasRenderingContext2D, u: number, crest: Fighter["crest"]): void {
  g.save();
  g.lineCap = "round";
  switch (crest) {
    case "halo":
      g.strokeStyle = "#fff6d6";
      g.lineWidth = 0.07 * u;
      ell(g, -0.05 * u, -3.14 * u, 0.42 * u, 0.13 * u);
      g.stroke();
      break;
    case "bolt":
      g.strokeStyle = PALETTE.gold;
      g.lineWidth = 0.06 * u;
      g.beginPath();
      g.moveTo(-0.5 * u, -2.42 * u);
      g.lineTo(-0.64 * u, -2.16 * u);
      g.lineTo(-0.5 * u, -2.18 * u);
      g.lineTo(-0.68 * u, -1.9 * u);
      g.stroke();
      break;
    case "ember":
      g.fillStyle = PALETTE.gold;
      for (let i = 0; i < 3; i++) {
        circle(g, (-1.34 - i * 0.12) * u, (-1.9 + i * 0.3) * u, (0.09 - i * 0.015) * u);
        g.fill();
      }
      break;
    case "frost":
    case "gale":
      g.fillStyle = PALETTE.purpleLt;
      for (let i = 0; i < 3; i++) {
        circle(g, (-1.34 - i * 0.12) * u, (-1.9 + i * 0.3) * u, (0.09 - i * 0.015) * u);
        g.fill();
      }
      break;
    case "shadow":
      g.strokeStyle = "rgba(139,92,246,0.9)";
      g.lineWidth = 0.05 * u;
      circle(g, -0.56 * u, -2.16 * u, 0.16 * u);
      g.stroke();
      break;
    default:
      // blade / cannon / quill / gaff: a small gold patch on the hood.
      g.fillStyle = PALETTE.gold;
      circle(g, -0.56 * u, -2.16 * u, 0.14 * u);
      g.fill();
      g.fillStyle = "#fff6d6";
      circle(g, -0.6 * u, -2.2 * u, 0.045 * u);
      g.fill();
  }
  g.restore();
}

// ---- Hand-sign badge ----------------------------------------------------------
//
// Iconic RPS hand-sign glyph in a badge — drawn next to each fighter on the
// reveal so the thrown hand always reads regardless of which throw sprite (or
// fallback) rendered. talon = fist, wing = flat mitt, beak = snipping V.

export function drawHandSign(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  move: "talon" | "wing" | "beak",
  color: string = PALETTE.gold,
): void {
  ctx.save();
  // Badge plate.
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fillStyle = "rgba(10,10,14,0.85)";
  ctx.fill();
  ctx.lineWidth = Math.max(1.5, r * 0.12);
  ctx.strokeStyle = color;
  ctx.stroke();

  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  const u = r / 10;
  if (move === "talon") {
    // ROCK — balled fist: solid disc with four knuckle bumps on top.
    ctx.beginPath();
    ctx.arc(cx, cy + u, 5 * u, 0, Math.PI * 2);
    ctx.fill();
    for (let i = 0; i < 4; i++) {
      const a = Math.PI * (0.15 + 0.23 * i) + Math.PI; // across the top
      ctx.beginPath();
      ctx.arc(cx + Math.cos(a) * 4.6 * u, cy + u + Math.sin(a) * 4.6 * u, 1.6 * u, 0, Math.PI * 2);
      ctx.fill();
    }
  } else if (move === "wing") {
    // PAPER — flat open mitt: rounded slab with finger seams.
    roundRect(ctx, cx - 5 * u, cy - 4 * u, 10 * u, 8 * u, 2.4 * u);
    ctx.fill();
    ctx.strokeStyle = "rgba(10,10,14,0.9)";
    ctx.lineWidth = 1.1 * u;
    for (let i = -1; i <= 1; i++) {
      ctx.beginPath();
      ctx.moveTo(cx + i * 2.4 * u, cy - 3.4 * u);
      ctx.lineTo(cx + i * 2.4 * u, cy + 1.2 * u);
      ctx.stroke();
    }
  } else {
    // SCISSORS — snipping V from a base knuckle.
    ctx.lineWidth = 2 * u;
    ctx.beginPath();
    ctx.moveTo(cx - 0.5 * u, cy + 4 * u);
    ctx.lineTo(cx - 3.6 * u, cy - 4.4 * u);
    ctx.moveTo(cx - 0.5 * u, cy + 4 * u);
    ctx.lineTo(cx + 3.6 * u, cy - 4.4 * u);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(cx - 0.5 * u, cy + 4.4 * u, 2.2 * u, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

// ---- Logo ---------------------------------------------------------------------

export function drawLogo(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  scale: number,
  img: HTMLImageElement | null,
  t: number,
): void {
  if (img) {
    const w = scale * 6;
    const h = (img.height / img.width) * w;
    ctx.drawImage(img, cx - w / 2, cy - h / 2, w, h);
    return;
  }
  ctx.save();
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  const pulse = 1 + Math.sin(t * 2) * 0.02;
  // Glow.
  const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, scale * 5);
  g.addColorStop(0, "rgba(139,92,246,0.35)");
  g.addColorStop(1, "rgba(139,92,246,0)");
  ctx.fillStyle = g;
  ctx.fillRect(cx - scale * 5, cy - scale * 3, scale * 10, scale * 6);

  ctx.font = `900 ${scale * 2 * pulse}px ${FONT_DISPLAY}`;
  ctx.fillStyle = PALETTE.purpleLt;
  ctx.fillText("RO-SHAM-BO", cx, cy - scale * 0.1);
  ctx.lineWidth = 2;
  ctx.strokeStyle = PALETTE.purple;
  ctx.strokeText("RO-SHAM-BO", cx, cy - scale * 0.1);

  ctx.font = `700 ${scale * 0.5}px ${FONT_MONO}`;
  ctx.fillStyle = PALETTE.gold;
  ctx.fillText("CHICKEN ARCADE", cx, cy + scale * 1.1);
  ctx.restore();
}

// ---- colour helpers -----------------------------------------------------------

function clampByte(n: number): number {
  return Math.max(0, Math.min(255, Math.round(n)));
}
function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}
function rgbToHex(r: number, g: number, b: number): string {
  return `#${[r, g, b].map((c) => clampByte(c).toString(16).padStart(2, "0")).join("")}`;
}
export function lighten(hex: string, amt: number): string {
  const [r, g, b] = hexToRgb(hex);
  return rgbToHex(r + (255 - r) * amt, g + (255 - g) * amt, b + (255 - b) * amt);
}
export function darken(hex: string, amt: number): string {
  const [r, g, b] = hexToRgb(hex);
  return rgbToHex(r * (1 - amt), g * (1 - amt), b * (1 - amt));
}
